/**
 * Full-screen forge workbench: conversation on the left (the Arche Builder's
 * conversation pane + prompt input, reused), status / screenshots / test
 * builds / production on the right.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  PluginConversationPane,
  ActivityStrip,
} from '../../plugin-builder/components/PluginConversationPane';
import { PluginPromptInput } from '../../plugin-builder/components/PluginPromptInput';
import { useForge } from '../hooks/useForge';
import { useForgeTurn } from '../hooks/useForgeTurn';
import { ForgeStatusBar } from '../components/ForgeStatusBar';
import { ForgeScreenshots } from '../components/ForgeScreenshots';
import { ForgeTestBuilds } from '../components/ForgeTestBuilds';
import { ForgePublishPanel } from '../components/ForgePublishPanel';
import { FORGE_ARCHE_ID } from '../constants';

export function ForgePage() {
  const { forgeId } = useParams<{ forgeId: string }>();
  const forgeState = useForge(forgeId ?? null);
  const turn = useForgeTurn(forgeId ?? null);
  const [prompt, setPrompt] = useState('');

  const { forge } = forgeState;
  const isRunning = forge?.status === 'running';

  const submit = () => {
    const text = prompt.trim();
    if (!text || !isRunning) return;
    setPrompt('');
    void turn.sendTurn(text);
  };

  if (forgeState.isLoading || (!forge && !forgeState.error)) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!forge) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">{forgeState.error ?? 'Forge not found'}</p>
        <Button asChild variant="outline">
          <Link to={`/a/${FORGE_ARCHE_ID}`}>Back to forges</Link>
        </Button>
      </div>
    );
  }

  const header = (
    <div className="flex items-center gap-3 border-b px-4 py-2">
      <Button asChild variant="ghost" size="sm">
        <Link to={`/a/${FORGE_ARCHE_ID}`}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Forges
        </Link>
      </Button>
      <span className="font-mono text-sm truncate" title={forge.archeId}>
        {forge.archeId}
      </span>
    </div>
  );

  const placeholder = isRunning
    ? 'Tell Claude Code what to build or change…'
    : 'Start the forge to send a turn.';

  return (
    <div className="h-screen flex flex-col">
      {header}
      {forgeState.error && (
        <div role="alert" className="border-b bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {forgeState.error}
        </div>
      )}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex flex-col min-w-0 border-r">
          <PluginConversationPane
            messages={turn.messages}
            isProcessing={turn.isProcessing}
            streamingText={turn.streamingText}
            lastFailure={turn.lastFailure}
            onRetry={() => void turn.retry()}
            // The pane gates its tool timeline on this flag (builder verbose
            // mode). A forge turn IS its tool calls, so it is always shown.
            isAdmin
            pendingVerboseEvents={turn.pendingToolEvents}
            verboseHistory={turn.toolEventHistory}
            emptyStateContent={
              <div className="flex h-full items-center justify-center px-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {isRunning
                    ? 'Describe the arche you want. Claude Code builds it in this forge and sends screenshots as it goes.'
                    : 'Start the forge, then describe the arche you want.'}
                </p>
              </div>
            }
            footerContent={
              <>
                {turn.activity && turn.isProcessing && <ActivityStrip label={turn.activity} />}
                <PluginPromptInput
                  value={prompt}
                  onChange={setPrompt}
                  onSubmit={submit}
                  onStopGeneration={() => void turn.stopTurn()}
                  isProcessing={turn.isProcessing}
                  hasMessages={turn.messages.length > 0}
                  placeholder={placeholder}
                />
              </>
            }
          />
        </div>
        <aside className="w-80 xl:w-96 flex-shrink-0 overflow-y-auto p-3 space-y-3">
          <ForgeStatusBar
            forge={forge}
            onStart={() => void forgeState.start()}
            onStop={() => void forgeState.stop()}
            // A refusal (the Pro gate, a running forge) is an expected outcome for
            // these two controls and is already surfaced through `forgeState.error`.
            onSetModelTier={(tier) => forgeState.setModelTier(tier).catch(() => undefined)}
            onSetSize={(size) => forgeState.setSize(size).catch(() => undefined)}
            isMutating={forgeState.isMutating}
          />
          <ForgeScreenshots screenshots={turn.screenshots} onClear={turn.clearScreenshots} />
          <ForgeTestBuilds
            archeId={forge.archeId}
            builds={forgeState.testBuilds}
            readyBuild={forgeState.readyTestBuild}
            onBuild={() => void forgeState.requestTestBuild()}
            canBuild={isRunning && !turn.isProcessing}
            isBuildRequested={forgeState.isBuildRequested}
          />
          <ForgePublishPanel
            forge={forge}
            submission={forgeState.submission}
            onPublish={(options) => void forgeState.publish(options)}
            canPublish={isRunning && !turn.isProcessing}
            isPublishRequested={forgeState.isPublishRequested}
          />
        </aside>
      </div>
    </div>
  );
}
